import { useState, useEffect, useCallback, useMemo } from "react";
import { loadFromSupabase, saveToSupabase } from "../../supabaseDB";
import { supabase } from "../../supabaseClient";

/* ═══════════════════════════════════════════════════════════════════════════
   AUDIT COMMENTS — observation threads with chat.

   Each OBSERVATION is a topic with a title/subject (a top-level row,
   parentId = null, its `body` holds the title). Under each observation
   there is a CHAT: messages exchanged by admin (GFI) and advisor
   (replies, parentId = the observation's uuid). Like a messaging app:
   type in the row, press send, the message joins that observation's
   thread. A new observation starts its own separate chat bubble.

   Scope:
     auditId      which audit
     targetType   'audit' | 'document'
     targetId     null for audit; report/doc id for document
     author       { name, role } — who is posting
     lang         'es' | 'en'

   Rows have no legacy_id, so rowToRecord leaves `id` empty and puts the
   real uuid in `_uuid`. Always read the id via cid().
   ═══════════════════════════════════════════════════════════════════════════ */

const T = {
  es: {
    title: "Observaciones",
    newObs: "Nueva observación", newObsTitle: "Título de la observación…",
    create: "Crear", cancel: "Cancelar",
    msgPlaceholder: "Escribe un mensaje…",
    empty: "No hay observaciones todavía.",
    loading: "Cargando…",
    advisor: "Asesor", admin: "GFI",
    delObs: "Eliminar observación", delObsConfirm: "¿Eliminar esta observación y todos sus mensajes?",
    delMsg: "Eliminar mensaje", noMsgs: "Sin mensajes aún.",
  },
  en: {
    title: "Remarks",
    newObs: "New remark", newObsTitle: "Remark title…",
    create: "Create", cancel: "Cancel",
    msgPlaceholder: "Write a message…",
    empty: "No remarks yet.",
    loading: "Loading…",
    advisor: "Advisor", admin: "GFI",
    delObs: "Delete remark", delObsConfirm: "Delete this remark and all its messages?",
    delMsg: "Delete message", noMsgs: "No messages yet.",
  },
};

function fmtWhen(iso, lang) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(lang === "es" ? "es-MX" : "en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch (e) { return ""; }
}

function roleLabel(role, t) {
  if (role === "audit_advisor" || role === "advisor") return t.advisor;
  return t.admin;
}

/* The real DB uuid (rowToRecord puts it in _uuid since there's no legacy_id). */
function cid(c) { return (c && (c._uuid || c.id)) || ""; }

export default function AuditComments({ auditId, targetType, targetId, author, lang }) {
  const t = T[lang === "en" ? "en" : "es"];
  const tType = targetType || "audit";
  const tId = targetId || null;

  const [all, setAll] = useState(null); /* null = loading */
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [msgDrafts, setMsgDrafts] = useState({}); /* { observationId: text } */
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const rows = await loadFromSupabase("audit_comments", { force: true });
      setAll(Array.isArray(rows) ? rows : []);
    } catch (e) { setAll([]); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  /* Comments for this exact target. */
  const scoped = useMemo(() => {
    const arr = Array.isArray(all) ? all : [];
    return arr.filter((c) => {
      if (String(c.auditId) !== String(auditId)) return false;
      if ((c.targetType || "audit") !== tType) return false;
      if (tType === "document") return String(c.targetId || "") === String(tId || "");
      return true;
    });
  }, [all, auditId, tType, tId]);

  /* Observations (top-level) + their chat messages (replies). */
  const observations = useMemo(() => {
    const tops = scoped.filter((c) => !c.parentId);
    tops.sort((a, b) => String(a.createdAt || "").localeCompare(String(b.createdAt || "")));
    return tops.map((obs) => {
      const msgs = scoped.filter((c) => String(c.parentId || "") === String(cid(obs)));
      msgs.sort((a, b) => String(a.createdAt || "").localeCompare(String(b.createdAt || "")));
      return { obs: obs, msgs: msgs };
    });
  }, [scoped]);

  /* Create a new observation (top-level row; body = the title). */
  const createObservation = useCallback(async () => {
    const title = (newTitle || "").trim();
    if (!title || busy) return;
    setBusy(true);
    try {
      await saveToSupabase("audit_comments", {
        auditId: auditId, targetType: tType, targetId: tId, parentId: null,
        authorName: (author && author.name) || "", authorRole: (author && author.role) || "",
        body: title, createdAt: new Date().toISOString(),
      });
      await reload();
      setNewTitle(""); setCreating(false);
    } finally { setBusy(false); }
  }, [newTitle, busy, auditId, tType, tId, author, reload]);

  /* Send a message inside an observation (reply row). */
  const sendMessage = useCallback(async (obsId) => {
    const text = (msgDrafts[obsId] || "").trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      await saveToSupabase("audit_comments", {
        auditId: auditId, targetType: tType, targetId: tId, parentId: obsId,
        authorName: (author && author.name) || "", authorRole: (author && author.role) || "",
        body: text, createdAt: new Date().toISOString(),
      });
      await reload();
      setMsgDrafts((p) => ({ ...p, [obsId]: "" }));
    } finally { setBusy(false); }
  }, [msgDrafts, busy, auditId, tType, tId, author, reload]);

  /* Delete a single message. */
  const deleteMessage = useCallback(async (msg) => {
    const id = cid(msg);
    if (!id) return;
    try { if (!window.confirm(t.delMsg + "?")) return; } catch (e) {}
    try { await supabase.from("audit_comments").delete().eq("id", id); await reload(); } catch (e) {}
  }, [reload, t.delMsg]);

  /* Delete a whole observation + its messages. */
  const deleteObservation = useCallback(async (obs, msgs) => {
    const id = cid(obs);
    if (!id) return;
    try { if (!window.confirm(t.delObsConfirm)) return; } catch (e) {}
    try {
      for (let i = 0; i < (msgs || []).length; i++) {
        await supabase.from("audit_comments").delete().eq("id", cid(msgs[i]));
      }
      await supabase.from("audit_comments").delete().eq("id", id);
      await reload();
    } catch (e) {}
  }, [reload, t.delObsConfirm]);

  const isMine = (c) => author && c.authorName === author.name && c.authorRole === author.role;

  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, background: "#fff", padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#1a2636", textTransform: "uppercase", letterSpacing: "0.04em" }}>{t.title}</div>
        {!creating && (
          <button onClick={() => { setCreating(true); setNewTitle(""); }} style={{ background: "#5a7d3c", color: "#fff", border: "none", borderRadius: 6, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ {t.newObs}</button>
        )}
      </div>

      {/* New observation composer */}
      {creating && (
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
          <input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder={t.newObsTitle}
            onKeyDown={(e) => { if (e.key === "Enter") createObservation(); }}
            autoFocus
            style={{ flex: 1, border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 11px", fontSize: 13, fontFamily: "inherit" }}
          />
          <button onClick={createObservation} disabled={busy || !newTitle.trim()} style={{ background: "#1a2636", color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer", opacity: (busy || !newTitle.trim()) ? 0.6 : 1 }}>{t.create}</button>
          <button onClick={() => { setCreating(false); setNewTitle(""); }} style={{ background: "transparent", color: "#666", border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 14px", fontSize: 12, cursor: "pointer" }}>{t.cancel}</button>
        </div>
      )}

      {all === null ? (
        <div style={{ padding: 16, textAlign: "center", color: "#888", fontSize: 12 }}>{t.loading}</div>
      ) : observations.length === 0 && !creating ? (
        <div style={{ padding: 14, textAlign: "center", color: "#999", fontSize: 12, fontStyle: "italic" }}>{t.empty}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {observations.map(({ obs, msgs }) => {
            const obsId = cid(obs);
            return (
              <div key={obsId} style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                {/* Observation header (title) */}
                <div style={{ background: "#1a2636", color: "#fff", padding: "8px 12px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{obs.body}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", whiteSpace: "nowrap" }}>{roleLabel(obs.authorRole, t)} · {fmtWhen(obs.createdAt, lang)}</span>
                    {isMine(obs) && (
                      <button onClick={() => deleteObservation(obs, msgs)} title={t.delObs} style={{ background: "transparent", border: "none", color: "#ff9b9b", cursor: "pointer", fontSize: 13, lineHeight: 1, padding: 0 }}>✕</button>
                    )}
                  </div>
                </div>

                {/* Chat messages */}
                <div style={{ padding: "10px 12px", background: "#fafbfc", display: "flex", flexDirection: "column", gap: 8 }}>
                  {msgs.length === 0 ? (
                    <div style={{ fontSize: 11, color: "#aaa", fontStyle: "italic", textAlign: "center", padding: "4px 0" }}>{t.noMsgs}</div>
                  ) : msgs.map((m) => {
                    const mine = isMine(m);
                    return (
                      <div key={cid(m)} style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start" }}>
                        <div style={{ maxWidth: "78%", background: mine ? "#dcf8c6" : "#fff", border: "1px solid " + (mine ? "#c5eba8" : "#e5e7eb"), borderRadius: 8, padding: "6px 10px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, marginBottom: 2 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: "#5a7d3c" }}>{roleLabel(m.authorRole, t)}</span>
                            <span style={{ fontSize: 9, color: "#999", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 6 }}>
                              {fmtWhen(m.createdAt, lang)}
                              {mine && <button onClick={() => deleteMessage(m)} title={t.delMsg} style={{ background: "transparent", border: "none", color: "#b42318", cursor: "pointer", fontSize: 11, lineHeight: 1, padding: 0 }}>✕</button>}
                            </span>
                          </div>
                          <div style={{ fontSize: 13, color: "#222", lineHeight: 1.4, whiteSpace: "pre-wrap" }}>{m.body}</div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Message composer — input + send on the same row */}
                  <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                    <input
                      value={msgDrafts[obsId] || ""}
                      onChange={(e) => setMsgDrafts((p) => ({ ...p, [obsId]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") sendMessage(obsId); }}
                      placeholder={t.msgPlaceholder}
                      style={{ flex: 1, border: "1px solid #d1d5db", borderRadius: 18, padding: "8px 14px", fontSize: 13, fontFamily: "inherit", background: "#fff" }}
                    />
                    <button
                      onClick={() => sendMessage(obsId)}
                      disabled={busy || !(msgDrafts[obsId] || "").trim()}
                      style={{ background: "#5a7d3c", color: "#fff", border: "none", borderRadius: 18, width: 38, height: 38, fontSize: 16, cursor: "pointer", opacity: (busy || !(msgDrafts[obsId] || "").trim()) ? 0.5 : 1, flexShrink: 0 }}
                      title="Enviar"
                    >&#10148;</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
